import { supabase } from "@/integrations/supabase/client";
import { hasConsent } from "@/lib/cookie-consent";

type Source = "support" | "search";

const recentlyLogged = new Map<string, number>();

/**
 * Quietly records what someone typed into the support bubble or the search box
 * so we can see the most common topics. Fire-and-forget: never blocks the UI
 * and never surfaces an error to the person typing.
 */
export function logQuery(source: Source, rawQuery: string) {
  if (typeof window === "undefined") return;
  // Non-essential analytics: never record typed searches without consent.
  if (!hasConsent("analytics")) return;
  const query = rawQuery.trim().slice(0, 500);
  if (query.length < 2) return;

  // De-duplicate the same phrase within a short window (search fires per keystroke)
  const key = `${source}:${query.toLowerCase()}`;
  const now = Date.now();
  const last = recentlyLogged.get(key);
  if (last && now - last < 60_000) return;
  recentlyLogged.set(key, now);
  if (recentlyLogged.size > 200) {
    for (const [k, t] of recentlyLogged) {
      if (now - t > 300_000) recentlyLogged.delete(k);
    }
  }

  void (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      await supabase.from("query_logs").insert({
        source,
        query,
        route: window.location.pathname,
        user_id: data.session?.user.id ?? null,
      });
    } catch {
      /* logging must never disrupt the experience */
    }
  })();
}
